import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Clock, Shield, Heart } from "lucide-react";
import { BRAND } from "../lib/brand";

export default function Book() {
  useEffect(() => {
    // Lazily load Calendly inline embed script
    const id = "calendly-widget-script";
    if (!document.getElementById(id)) {
      const s = document.createElement("script");
      s.id = id;
      s.src = "https://assets.calendly.com/assets/external/widget.js";
      s.async = true;
      document.body.appendChild(s);
    }
  }, []);

  return (
    <div data-testid="page-book">
      {/* Header */}
      <section className="pt-16 pb-10 sm:pt-24 sm:pb-12 bg-lilac-soft relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-35" />
        <div className="relative max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 text-center">
          <p className="text-sm uppercase tracking-[0.25em] text-lilac-deep font-semibold reveal">
            Reserve Your Time
          </p>
          <h1 className="reveal reveal-d1 mt-5 font-serif-display text-5xl sm:text-6xl lg:text-7xl font-bold text-ink leading-[1.02] tracking-tight">
            Thirty minutes.
            <span className="block italic text-lilac-deep">One quiet conversation.</span>
          </h1>
          <p className="reveal reveal-d2 mt-6 text-lg sm:text-xl text-charcoal max-w-2xl mx-auto leading-relaxed">
            Pick a time that suits your week. No pressure, no upsell —
            just a real plan you'll leave with, written for the life you actually live.
          </p>
        </div>
      </section>

      {/* Layout: Calendly + Sidebar */}
      <section className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-3 gap-10 lg:gap-12">
          {/* Calendly embed */}
          <div className="lg:col-span-2">
            <div className="rounded-3xl border border-border bg-white p-3 sm:p-5 shadow-sm">
              <div
                data-testid="calendly-embed"
                className="calendly-inline-widget rounded-2xl overflow-hidden"
                data-url={BRAND.calendly}
                style={{ minWidth: "320px", height: "740px" }}
              />
            </div>
            <p className="text-xs text-charcoal text-center mt-4">
              Trouble seeing the calendar?{" "}
              <a
                href={BRAND.calendly}
                target="_blank"
                rel="noreferrer"
                data-testid="calendly-fallback-link"
                className="text-lilac-deep underline underline-offset-4 font-semibold hover:text-ink"
              >
                Open Calendly in a new tab →
              </a>
            </p>
          </div>

          {/* Sidebar */}
          <aside className="space-y-6">
            <div className="rounded-3xl bg-mint p-7">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 rounded-full bg-ink text-mint flex items-center justify-center">
                  <Calendar size={18} />
                </div>
                <h3 className="font-serif-display text-2xl font-bold text-ink">What to expect</h3>
              </div>
              <ul className="space-y-3 text-sm text-ink/85">
                <li className="flex gap-3">
                  <Clock size={16} className="shrink-0 mt-0.5 text-ink" />
                  <span>30 minutes, on Zoom or by phone — your call.</span>
                </li>
                <li className="flex gap-3">
                  <Heart size={16} className="shrink-0 mt-0.5 text-ink" />
                  <span>We'll talk about your real life — the trips, the closet, the goals.</span>
                </li>
                <li className="flex gap-3">
                  <Shield size={16} className="shrink-0 mt-0.5 text-ink" />
                  <span>You leave with a one-page plan. No upsell required to use it.</span>
                </li>
              </ul>
            </div>

            <div className="rounded-3xl bg-white border border-border p-7">
              <h3 className="font-serif-display text-xl font-bold text-ink">
                Prefer to start on your own?
              </h3>
              <p className="text-sm text-charcoal mt-3 leading-relaxed">
                The 2-Hour Course and the Digital Vault are both available now —
                no call needed.
              </p>
              <Link
                to="/services"
                data-testid="book-sidebar-services-cta"
                className="mt-5 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-ink border border-ink/15 hover:border-ink/40 transition-colors"
              >
                See the services <ArrowRight size={15} />
              </Link>
            </div>

            <div className="rounded-3xl bg-lilac-soft p-7">
              <p className="font-serif-display italic text-ink text-lg leading-snug">
                "She didn't sell me anything. She gave me a plan, and I implemented
                it before our second call."
              </p>
              <div className="mt-4 text-xs text-charcoal">— Marisol R., Houston</div>
            </div>
          </aside>
        </div>
      </section>

      {/* Reassurance band */}
      <section className="pb-24 sm:pb-32">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="rounded-[2rem] bg-ink text-white p-10 sm:p-14 text-center relative overflow-hidden">
            <div className="absolute -top-20 -left-20 h-72 w-72 rounded-full bg-mint-deep/30 blur-3xl" />
            <div className="absolute -bottom-20 -right-20 h-72 w-72 rounded-full bg-lilac-deep/30 blur-3xl" />
            <div className="relative">
              <h2 className="font-serif-display text-3xl sm:text-4xl font-bold leading-[1.1]">
                The booking link not loading?
              </h2>
              <p className="mt-4 text-white/75 max-w-xl mx-auto">
                Calendly will replace this with my live calendar in seconds. If
                anything looks off, just open it directly — same calendar, same times.
              </p>
              <a
                href={BRAND.calendly}
                target="_blank"
                rel="noreferrer"
                data-testid="book-final-cta-direct"
                className="mt-8 inline-flex items-center gap-2 rounded-full px-7 py-3.5 bg-mint text-ink font-semibold hover:bg-mint/85 transition-colors"
              >
                Open my Calendly <ArrowRight size={18} />
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
